import ResponsiveModal from "@/modules/common/components/ui-kit/modals/responsive-modal";
import {
  Box,
  Button,
  CircularProgress,
  Divider,
  GlobalStyles,
  Skeleton,
  Stack,
  Typography,
  useTheme,
} from "@mui/material";
import { CircleAlert, CircleCheck, Printer, TriangleAlert, Upload } from "lucide-react";
import { useEffect, useMemo, useState } from "react";
import {FormField, FormSection, NoteAttachmentView, StepGlobal, StepUpload} from "../dtos";
import { toast } from "../utils/toast";
import { useStepDetail } from "../hooks/use-step-detail.hook";
import { useStepMutations } from "../hooks/use-step-mutations.hook";
import DocumentField from "./document-field";
import { primaryButtonSx, secondaryButtonSx } from "./field-shell";
import FormFieldInput from "./form-fields";
import { kycDisplayFields, kycFieldKey } from "./kyc-card-panel";
import { fieldHiddenByRule, sectionHiddenByRule } from "../utils/conditions";
import {NoteAttachmentList} from "./note-attachments";

/**
 * §Modals: the KYC modal — Figma 12107:43906 (read-only document) / 12107:44232 (editable document
 * pane + nested "Upload signed KYC"), 1032×720. Per the 2026-07-30 decision this is the ONLY place
 * KYC is edited: the step card shrank to the design's progress panel (kyc-card-panel.tsx).
 *
 * Two columns split by a divider:
 *   LEFT  — the captured data as tinted section panels (label/value rows) under a state band.
 *           This pane is also the print region ("Print document").
 *   RIGHT — the document pane: the design's editable form when the agent can work the step
 *           (frame 44232), the dashed read-only page otherwise (frame 43906).
 *
 * §EngineErrors (decision 2026-07-30: "errors should be from the engine, handled dynamically"):
 * the red band is NOT hardcoded — when the engine sends the step back (REWORK_REQUIRED / REJECTED),
 * the band renders the reviewer's own comment. Field-level verdicts (frame 62738's "Name doesn't
 * match NID") render the same way once the engine carries per-field verdicts — no data exists yet.
 */
const present = (value: unknown): boolean =>
  value !== undefined && value !== null && value !== "";

const fieldValue = (field: FormField, raw: unknown): string => {
  if (raw === undefined || raw === null || raw === "") return "—";
  const t = (field.t ?? "").toLowerCase();
  if (t.includes("bool") || t.includes("check")) return raw ? "Yes" : "No";
  if (t.includes("money") || t.includes("amount")) {
    const n = Number(raw);
    return Number.isFinite(n) ? `${Math.round(n).toLocaleString("en-US")} EGP` : String(raw);
  }
  return String(raw);
};

/** the step's KYC document placement — where a signed copy is stored */
const signedPlacementOf = (globals: StepGlobal[]): StepGlobal | undefined =>
  globals.find(
    (global) =>
      global.active !== false &&
      (global.type === "document" || global.document !== undefined) &&
      global.mode !== "view"
  );

/**
 * §KYC-form: the placement whose document workflow-manager can RENDER from the step's answers —
 * the official Arabic «نموذج اعرف عميلك». WM advertises it as `document.generator` on an `edit`
 * placement only (a view placement, e.g. Credit Review — KYC, deliberately gets nothing).
 */
const generatorPlacementOf = (globals: StepGlobal[]): StepGlobal | undefined =>
  globals.find(
    (global) =>
      global.active !== false &&
      global.mode !== "view" &&
      global.document?.generator !== undefined
  );

/** WM names the file per party; mirror its wording so the download matches what WM produces. */
const PARTY_LABEL: Record<string, string> = {
  PRIMARY: "Primary",
  SECONDARY: "Joint",
  TERTIARY: "Guarantor",
};

const KycModal = ({
  stepUid,
  open,
  onClose,
  agentViewOnly = false,
}: {
  stepUid: string;
  open: boolean;
  onClose: () => void;
  /** §AgentView: the step's authored AGENT mode is 'view' — the pane renders read-only */
  agentViewOnly?: boolean;
}) => {
  const theme = useTheme();
  const c = theme.nawy.colors.semantic;
  // §MobileQA: the CRM theme maps semantic Layer/Secondary to navy[50] (#EEF3F5) and the
  // *Light extension grounds to the 50 shade, while the design draws this sheet on
  // Layer/Secondary #F4F7F8 and the Indicator ramp (green/yellow 100). Reach for the
  // primitives for those three, the way step-card.tsx already does for the card ground.
  const primitives = theme.nawy.colors.primitives;
  const { detail, isLoading } = useStepDetail({ stepUid, enabled: open });
  const mutations = useStepMutations({ stepUid });
  const [uploadOpen, setUploadOpen] = useState(false);

  const schema = useMemo(
    () => (detail?.formSchema ?? []) as FormSection[],
    [detail?.formSchema]
  );
  const data = useMemo(
    () => (detail?.submissions?.[0]?.data as Record<string, unknown>) ?? {},
    [detail?.submissions]
  );

  // the editable pane's live buffer, seeded from the latest submission
  const [values, setValues] = useState<Record<string, unknown>>(data);
  useEffect(() => setValues(data), [data]);

  // §45–§47 (QA): conditional fields hide/reveal LIVE as the gating answer changes — the same
  // precondition rules the engine evaluates, judged here against the merged draft so the modal
  // matches the Workflow Manager's behaviour between saves.
  const sectionsFor = (context: Record<string, unknown>) =>
    schema
      .filter((section) => !sectionHiddenByRule(section, context))
      .map((section) => ({
        section,
        fields: (section.fields ?? []).filter(
          (f) =>
            f.l && f.t && f.t !== "document" && f.t !== "file" && !fieldHiddenByRule(f, context)
        ),
      }))
      .filter((row) => row.fields.length);
  const allFields = kycDisplayFields(schema, data);
  const filledCount = allFields.filter(({ section, field }) => {
    const value = data[kycFieldKey(section, field)];
    return value !== undefined && value !== null && value !== "";
  }).length;
  const state: "empty" | "partial" | "filled" =
    filledCount === 0 ? "empty" : filledCount < allFields.length ? "partial" : "filled";

  const settled = detail?.status === "APPROVED" || detail?.status === "SKIPPED";
  const editable = Boolean(
    detail && (detail.agentAccess?.editable ?? true) && !settled && !agentViewOnly
  );
  const dirty = useMemo(
    () => Object.keys(values).some((key) => values[key] !== data[key]),
    [values, data]
  );
  // §Submission-REPLACES gotcha: always save the merged object, never the buffer alone.
  // §45–§47: values whose field is hidden under the merged draft (the gate flipped to لا) are
  // cleared — an orphaned dependent answer must never be submitted.
  const mergedSubmission = (): Record<string, unknown> => {
    const merged: Record<string, unknown> = { ...data, ...values };
    for (const section of schema) {
      for (const field of section.fields ?? []) {
        const key = kycFieldKey(section, field);
        if (
          present(merged[key]) &&
          (sectionHiddenByRule(section, merged) || fieldHiddenByRule(field, merged))
        ) {
          merged[key] = "";
        }
      }
    }
    return merged;
  };
  const save = () => {
    mutations.save.mutate({ data: mergedSubmission() });
  };

  // §EngineErrors: a sent-back step surfaces the reviewer's reason as the red band. The rework
  // engine RESTARTS the target step (IN_PROGRESS + reworkCount++, verified live 2026-07-30) — it
  // does not park it in a REWORK_REQUIRED status — so the signal is the rework comment itself:
  // sent back and not yet re-settled ⇒ the latest rework comment is the reason being fixed.
  const reworkComment = !settled
    ? [...(detail?.comments ?? [])]
        .reverse()
        .find((comment) => comment.isReworkComment)
    : undefined;
  const reworked =
    detail?.status === "REWORK_REQUIRED" ||
    detail?.status === "REJECTED" ||
    Boolean(reworkComment);

  /**
   * §VerdictNote: the reviewer's file-level verdicts on this step, in the reviewer's own words.
   *
   * The sheet reported only the STEP's status, so a KYC approved WITH CONDITIONS showed a green "Ready for
   * signing" band and said nothing at all about the condition — the agent had to open the separate
   * "Upload signed" sheet to discover there was one. A rejected FILE on a step whose own status is settled
   * was invisible the same way. Collected across every placement, not just the signed one, because a
   * condition can be attached to any document the step collects.
   */
  const fileVerdicts = (detail?.globals ?? [])
    .filter((global) => global.active !== false)
    .flatMap((global) => global.document?.uploads ?? [])
    .filter((upload) => !upload.generated && Boolean(upload.rejectionReason))
    .map((upload) => ({
      decision: (upload.decision ?? "").toUpperCase(),
      fileName: upload.fileName,
      note: upload.rejectionReason as string,
      attachments: upload.decisionAttachments,
    }));
  const conditionalNotes = fileVerdicts.filter((verdict) => verdict.decision === "CONDITIONAL");
  const rejectedNotes = fileVerdicts.filter((verdict) => verdict.decision === "REJECTED");

  const signed = signedPlacementOf(detail?.globals ?? []);
  // §Signed-upload (QA 2026-08-16): the generator's own SYSTEM file (`generated`) shares this
  // document slot for download — it is NOT the agent's signed copy, so the "Upload signed KYC"
  // sheet must start empty and list only human uploads.
  const signedUploads: StepUpload[] = (signed?.document?.uploads ?? [])
    .filter((upload) => !upload.generated)
    .map((upload) => ({
      uid: upload.uid,
      fileName: upload.fileName,
      fieldKey: signed!.ref,
      decision: upload.decision as StepUpload["decision"],
      rejectionReason: upload.rejectionReason ?? null,
      decisionAttachments: upload.decisionAttachments,
    }));

  const generatorPlacement = generatorPlacementOf(detail?.globals ?? []);
  const [generating, setGenerating] = useState(false);

  /**
   * §KYC-form: download the official «نموذج اعرف عميلك» that workflow-manager renders from this
   * step's answers — the same sequence WM's own step screen runs (step-screen.tsx
   * `generatePoolDocument`): flush pending edits, generate, report the saved filename, refresh.
   *
   * Saving first is load-bearing rather than defensive. Text edits sit in a debounce window, so a
   * form rendered from the previous draft is one the applicant would print, sign and upload —
   * silently committing them to answers already corrected on screen. The save is `quiet` because
   * its own "Changes saved" toast would race the download toast for the one action the agent took.
   */
  const downloadForm = async () => {
    if (!generatorPlacement || generating) return;
    setGenerating(true);
    try {
      // Generation WRITES (the server files the rendered docx on the card), so a settled step is
      // refused with "This step is already decided." — and most KYC steps in flight are settled.
      // The form already exists there in that case, filed by the completion effect, so serve the
      // filed copy rather than showing the agent an error for asking a reasonable question.
      if (!editable) {
        const uploads = generatorPlacement.document?.uploads ?? [];
        const latest = uploads[uploads.length - 1];
        if (latest) {
          await mutations.download(latest.uid, latest.fileName);
          return;
        }
      }
      if (dirty && editable) {
        await mutations.save.mutateAsync({ data: mergedSubmission(), quiet: true });
      }
      const party = PARTY_LABEL[String(detail?.party ?? "")] ?? "Applicant";
      const fileName = await mutations.generate(
        generatorPlacement.ref,
        `KYC Form - ${party}.docx`
      );
      if (fileName) toast.success(`${fileName} downloaded.`);
    } finally {
      setGenerating(false);
    }
  };

  /**
   * Print only the left recap pane. `window.print()` alone would spool the whole lead page,
   * so the print media query hides everything else and lifts the recap to the page origin.
   */
  const print = () => window.print();
  const printStyles = (
    <GlobalStyles
      styles={{
        "@media print": {
          "body *": { visibility: "hidden !important" },
          "#kyc-print-region, #kyc-print-region *": { visibility: "visible !important" },
          "#kyc-print-region": {
            // §MobileQA: the recap pane is display:none on a phone while the form pane is showing.
            // `visibility` can't resurrect a display:none subtree, so Print must restore it here.
            display: "block !important",
            position: "absolute !important",
            left: 0,
            top: 0,
            width: "100% !important",
            maxHeight: "none !important",
            overflow: "visible !important",
            padding: 0,
          },
        },
      }}
    />
  );

  /**
   * §VerdictNote: one band per verdict the reviewer left, in their words and in full.
   *
   * Rendered ABOVE the readiness band rather than instead of it, and both tones can show at once: a KYC can
   * legitimately be ready to sign AND carry a condition, and it can carry a condition on one file while
   * another was rejected outright. Collapsing them to a single band is what let the green "Ready for
   * signing" hide a live condition.
   */
  const verdictBand = (
    notes: {fileName: string; note: string; attachments?: NoteAttachmentView[]}[],
    // theme tokens resolve to ColorValue (string | undefined), which MUI's sx and lucide both accept
    tone: {fg?: string; bg?: string},
    label: string
  ) =>
    notes.length > 0 ? (
      <Stack
        spacing={0.75}
        sx={{ p: 1.5, borderRadius: theme.nawy.radius.lg, backgroundColor: tone.bg }}
      >
        {notes.map((entry, index) => (
          <Stack
            key={`${entry.fileName}-${index}`}
            direction="row"
            spacing={1}
            alignItems="flex-start"
          >
            <CircleAlert size={18} color={tone.fg} style={{ flexShrink: 0, marginTop: 1 }} />
            <Box sx={{ minWidth: 0 }}>
              <Typography sx={{ fontSize: 14, fontWeight: 600, color: tone.fg }}>{label}</Typography>
              {/* the reviewer's note is the instruction — never clamped, never ellipsised */}
              <Typography
                sx={{
                  fontSize: 13,
                  lineHeight: "18px",
                  color: tone.fg,
                  whiteSpace: "pre-wrap",
                  wordBreak: "break-word",
                }}
              >
                {entry.note}
              </Typography>
              <NoteAttachmentList attachments={entry.attachments} />
            </Box>
          </Stack>
        ))}
      </Stack>
    ) : null;

  const readinessBand = reworked ? (
    <Stack
      direction="row"
      spacing={1}
      alignItems="flex-start"
      sx={{
        p: 1.5,
        borderRadius: theme.nawy.radius.lg,
        backgroundColor: c.layer.extension.negativeLight,
      }}
    >
      <CircleAlert
        size={18}
        color={c.text.extension.negative}
        style={{ flexShrink: 0, marginTop: 1 }}
      />
      <Box sx={{minWidth: 0}}>
        <Typography
          sx={{
            fontSize: 14,
            fontWeight: 600,
            color: c.text.extension.negative,
            whiteSpace: "pre-wrap",
            wordBreak: "break-word",
          }}
        >
          {reworkComment?.content ?? "Sent back by the reviewer"}
        </Typography>
        <NoteAttachmentList attachments={reworkComment?.attachments} />
      </Box>
    </Stack>
  ) : state === "empty" ? null : (
    <Stack
      direction="row"
      spacing={1}
      alignItems="center"
      sx={{
        p: 1.5,
        // §MobileQA: both bands are the SAME block in the design — a radius-lg panel running the
        // full width to the sheet's gutters on the Indicator ramp (Green Light #CFE8E2 / Yellow
        // Light #F3F3C4) under near-black Text/Primary. The partial band had become an inset
        // full-radius pill on the orange warning ground, which reads as a different component.
        borderRadius: theme.nawy.radius.lg,
        backgroundColor:
          state === "filled" ? primitives.green[100] : primitives.yellow[100],
      }}
    >
      {state === "filled" ? (
        <CircleCheck size={18} color={c.text.extension.positive} />
      ) : (
        <TriangleAlert size={18} color={c.text.primary} />
      )}
      <Typography sx={{ fontSize: 14, color: c.text.primary }}>
        {state === "filled" ? "Ready for signing" : "Some fields are still missing"}
      </Typography>
    </Stack>
  );

  // §VerdictNote: rejections first (they close the door), then conditions, then readiness. `reworked` already
  // carries a step-level send-back, so a file rejection is not repeated underneath it.
  const rejectedBand = reworked
    ? null
    : verdictBand(
        rejectedNotes,
        {fg: c.text.extension.negative, bg: c.layer.extension.negativeLight},
        "Rejected by the reviewer"
      );
  const conditionalBand = verdictBand(
    conditionalNotes,
    {fg: "#E65F00", bg: "#FFF6F0"},
    "Approved with conditions"
  );
  // Stay NULL when there is nothing to say: the parent Stack spaces its children, so an empty wrapper
  // would open a gap where the old single band contributed none.
  const stateBand =
    rejectedBand || conditionalBand || readinessBand ? (
      <Stack spacing={1}>
        {rejectedBand}
        {conditionalBand}
        {readinessBand}
      </Stack>
    ) : null;

  const recapRow = (section: FormSection, field: FormField) => {
    const key = kycFieldKey(section, field);
    return (
      // §RTL: the questionnaire is bilingual — `dir="auto"` resolves each row from its OWN first
      // strong character, so an Arabic question lays out right-to-left while English rows stay LTR.
      <Box key={key} dir="auto">
        <Typography sx={{ fontSize: 11, lineHeight: "16px", color: c.text.secondary }}>
          {field.l}
        </Typography>
        <Typography sx={{ fontSize: 14, lineHeight: "20px", color: c.text.primary }}>
          {fieldValue(field, data[key])}
        </Typography>
      </Box>
    );
  };

  return (
    <>
      {open && printStyles}
      <ResponsiveModal open={open} onClose={onClose} title="KYC">
        <Stack
          direction={{ mobile: "column", desktop: "row" }}
          divider={
            <Divider
              flexItem
              orientation="vertical"
              sx={{ display: { mobile: "none", desktop: "block" } }}
            />
          }
          // §Mobile: ONE scroller for the stacked panes — per-pane scrolling belongs to the
          // side-by-side desktop split; two nested scroll areas in a column collapse the second
          // pane out of view on a phone.
          sx={{
            width: { mobile: "100%", desktop: 1032 },
            // §MobileQA (2026-08-16): on a phone the SHEET owns the scroll (MobileModal's
            // Sheet.Scroller). The 72vh inner scroller out-measured the visible sheet (vh is the
            // LARGE viewport on iOS), so the last question (PEP) and the action row fell beyond
            // reach — desktop keeps the bounded two-pane split.
            maxHeight: { mobile: "none", desktop: "72vh" },
            overflowY: { mobile: "visible", desktop: "visible" },
          }}
        >
          {/* LEFT — captured data (also the print region) */}
          <Box
            id="kyc-print-region"
            sx={{
              // §MobileQA: the design's mobile KYC sheet is a SINGLE column — every field appears
              // exactly once. Stacking the desktop two-pane split rendered the whole section set
              // TWICE on a phone (recap, then the same questions again). On mobile show the pane
              // that matches the mode: the form when the agent can fill it, this recap when they
              // can't. Both panes still render side by side on desktop, and Print re-shows this
              // region regardless (see printStyles).
              display: { mobile: editable ? "none" : "block", desktop: "block" },
              width: { mobile: "100%", desktop: 440 },
              p: 2,
              overflowY: { mobile: "visible", desktop: "auto" },
            }}
          >
            {isLoading ? (
              <Skeleton variant="rounded" width="100%" height={220} />
            ) : state === "empty" && !reworked ? (
              // §MobileQA: the design's "Empty state" (12107:44732) is a PANEL, not bare centred
              // text — Layer/Secondary ground, radius 2xl, p 16 / gap 12, a 24px triangle-alert
              // and the title/body pair both on Text/Primary (16/22 semibold over 14/20 regular).
              <Stack
                alignItems="center"
                spacing={1.5}
                sx={{
                  p: 2,
                  borderRadius: theme.nawy.radius["2xl"],
                  backgroundColor: primitives.white[200],
                }}
              >
                <TriangleAlert size={24} color={c.text.primary} />
                <Stack alignItems="center" spacing={1} sx={{ textAlign: "center" }}>
                  <Typography
                    sx={{
                      fontSize: 16,
                      lineHeight: "22px",
                      fontWeight: 600,
                      color: c.text.primary,
                    }}
                  >
                    No inputs yet
                  </Typography>
                  <Typography
                    sx={{ fontSize: 14, lineHeight: "20px", color: c.text.primary }}
                  >
                    Client has not filled out KYC yet
                  </Typography>
                </Stack>
              </Stack>
            ) : (
              <Stack spacing={2}>
                {stateBand}
                {sectionsFor(data).map(({ section, fields }) => (
                  <Box
                    key={section.name}
                    sx={{
                      p: 2,
                      borderRadius: theme.nawy.radius.xl,
                      // design "Section" (12107:44733) sits on Layer/Secondary #F4F7F8 —
                      // see the primitives note at the top of the component.
                      backgroundColor: primitives.white[200],
                    }}
                  >
                    {/* design section heading is Body/sm - Semibold: 12/18 weight 600 */}
                    <Typography
                      dir="auto"
                      sx={{
                        fontSize: 12,
                        lineHeight: "18px",
                        fontWeight: 600,
                        color: c.text.primary,
                        mb: 1.5,
                      }}
                    >
                      {section.name}
                    </Typography>
                    <Stack spacing={1.5}>
                      {fields.map((field) => recapRow(section, field))}
                    </Stack>
                  </Box>
                ))}
              </Stack>
            )}
          </Box>

          {/* RIGHT — the document pane: editable form (44232) or the dashed read-only page (43906).
              §Mobile: this pane IS the KYC (the questions) — it stacks under the recap on a phone;
              hiding it left mobile users with just the recap and the Print button. */}
          <Box
            sx={{
              // §MobileQA: the other half of the single-column rule — read-only on a phone is the
              // recap pane above, so this one steps aside instead of repeating it.
              display: { mobile: editable ? "block" : "none", desktop: "block" },
              flex: { desktop: 1 },
              p: 2,
              overflowY: { mobile: "visible", desktop: "auto" },
            }}
          >
            <Box
              sx={{
                p: 2.5,
                borderRadius: theme.nawy.radius.xl,
                border: `1px dashed ${c.border.primary}`,
                backgroundColor: c.layer.primary,
              }}
            >
              {isLoading ? (
                <Skeleton variant="rounded" width="100%" height={320} />
              ) : (
                <Stack spacing={editable ? 2.5 : 1.5}>
                  {sectionsFor(editable ? {...data, ...values} : data).map(({ section, fields }) => (
                    <Box key={section.name}>
                      {/* same Body/sm - Semibold section heading as the recap pane */}
                      <Typography
                        dir="auto"
                        sx={{
                          fontSize: 12,
                          lineHeight: "18px",
                          fontWeight: 600,
                          color: c.text.primary,
                          mb: 1,
                        }}
                      >
                        {section.name}
                      </Typography>
                      <Stack spacing={editable ? 2 : 1.25}>
                        {fields.map((field) =>
                          editable ? (
                            // §RTL: per-question direction — an Arabic question (and its options)
                            // lays out right-to-left; English ones stay LTR. dir="auto" resolves
                            // from the wrapped field's own label text.
                            <Box key={kycFieldKey(section, field)} dir="auto">
                              <FormFieldInput
                                field={field}
                                value={values[kycFieldKey(section, field)]}
                                onChange={(next) =>
                                  setValues((previous) => ({
                                    ...previous,
                                    [kycFieldKey(section, field)]: next,
                                  }))
                                }
                                disabled={field.mode === "view"}
                              />
                            </Box>
                          ) : (
                            recapRow(section, field)
                          )
                        )}
                      </Stack>
                    </Box>
                  ))}
                </Stack>
              )}
            </Box>
          </Box>
        </Stack>

        {/* §Mobile: up to three nowrap buttons (Save / Upload signed / Print document) in one
            un-wrapping row overflow a 390px sheet — wrap instead of clipping the last verb. */}
        <Stack
          direction="row"
          justifyContent="flex-end"
          spacing={1.5}
          useFlexGap
          sx={{
            px: 2.5,
            py: 2,
            flexWrap: "wrap",
            rowGap: 1.5,
            // §MobileQA (iOS Safari): this footer lives INSIDE MobileModal's scrolling child box,
            // so it scrolled with the content instead of staying pinned the way the design shows it.
            // Sticking it to the bottom of that scroller keeps the verbs reachable at any scroll
            // position; the negative offset cancels the sheet's own 16px padding so it sits flush.
            position: "sticky",
            bottom: -16,
            zIndex: 1,
            backgroundColor: c.layer.primary,
            borderTop: `1px solid ${c.border.secondary}`,
          }}
        >
          {/* deviation: the design's footer has no Save — without one, edits in the pane would
              silently vanish on close */}
          {editable && (
            <Button
              variant="outlined"
              disabled={!dirty || mutations.save.isLoading}
              startIcon={
                mutations.save.isLoading ? <CircularProgress size={14} /> : undefined
              }
              onClick={save}
              sx={secondaryButtonSx(theme)}
            >
              Save
            </Button>
          )}
          {signed && (
            <Button
              variant="outlined"
              startIcon={<Upload size={16} />}
              onClick={() => setUploadOpen(true)}
              sx={secondaryButtonSx(theme)}
            >
              Upload signed
            </Button>
          )}
          {/* §KYC-form: "Print" now downloads the official Arabic form workflow-manager renders
              from these answers, not a browser print of the recap pane. The recap print remains
              reachable through the media query below for anyone who wants the on-screen summary,
              but the artifact an agent actually needs is the signed-form template. */}
          <Button
            variant="contained"
            disableElevation
            disabled={generating || mutations.save.isPending}
            startIcon={
              generating ? (
                <CircularProgress size={16} sx={{ color: "inherit" }} />
              ) : (
                <Printer size={16} />
              )
            }
            onClick={generatorPlacement ? downloadForm : print}
            sx={primaryButtonSx(theme)}
          >
            {generatorPlacement ? "Print document" : "Print recap"}
          </Button>
        </Stack>
      </ResponsiveModal>

      {/* nested "Upload signed KYC" (Figma 12107:44232) — writes to the step's KYC placement */}
      {signed && (
        <ResponsiveModal
          open={uploadOpen}
          onClose={() => setUploadOpen(false)}
          title="Upload signed KYC"
        >
          <Box sx={{ p: 2.5, width: { mobile: "100%", desktop: 480 }, minHeight: 200 }}>
            <DocumentField
              // §Delete: carry the placement's own mode — the delete control mirrors the engine's
              // rule (editable placement only), and a synthetic field without a mode would decide it
              // on a default rather than on what WM actually authored for this slot.
              field={{
                l: signed.label ?? "Signed KYC",
                t: "document",
                ref: signed.ref,
                mode: signed.mode === "view" ? "view" : "edit",
              }}
              fieldKey={signed.ref}
              uploads={signedUploads}
              disabled={!editable}
              mutations={mutations}
            />
          </Box>
          <Stack
            direction="row"
            justifyContent="flex-end"
            sx={{ px: 2.5, py: 2, borderTop: `1px solid ${c.border.secondary}` }}
          >
            <Button
              variant="contained"
              disableElevation
              onClick={() => setUploadOpen(false)}
              sx={primaryButtonSx(theme)}
            >
              Save document
            </Button>
          </Stack>
        </ResponsiveModal>
      )}
    </>
  );
};

export default KycModal;
